import {Injectable} from '@angular/core';
import { ConversationStatus, MessageStatus, ContentMessageType } from '../models/chat';

// Enhanced notification template interfaces with state awareness
interface NotificationTemplate {
  title: string;
  body: string;
  icon?: string;
  tag?: string;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  requireInteraction?: boolean;
}

interface MessageNotificationData {
  senderName: string;
  campaignName: string;
  messagePreview: string;
  conversationId: string;
  messageType?: ContentMessageType;
  messageStatus?: MessageStatus;
  conversationStatus?: ConversationStatus;
}

interface ConversationNotificationData {
  campaignName: string;
  clientName: string;
  conversationId: string;
  status: ConversationStatus;
  previousStatus?: ConversationStatus;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  // Standard notification configuration
  private STANDARD_ICON = 'assets/Logo_Ecopetrol.png';
  private readonly NOTIFICATION_DURATION = 8000; // 8 seconds
  private readonly DEBOUNCE_TIME = 2000; // 2 seconds between notifications

  // Notification debouncing mechanism
  private lastNotificationTime = 0;
  private readonly NOTIFICATION_DEBOUNCE_TIME = this.DEBOUNCE_TIME;
  private activeNotifications = new Set<string>();

  // User preferences
  private soundEnabled = true;
  private browserNotificationsEnabled = true;

  constructor() {
    // Load user preferences from localStorage
    this.loadPreferences();

    const iconByWorkspace = JSON.parse(sessionStorage.getItem('workpsaceId')!);

    console.log(iconByWorkspace);

    if (iconByWorkspace.name != "Ecopetrol" || iconByWorkspace.name != "Atento Ecopetrol") {
      this.STANDARD_ICON = 'assets/logo.png'
    }

    // Ensure notification icon is available
    this.preloadNotificationIcon();
  }

  /**
   * Preload the notification icon to ensure it's available
   */
  private preloadNotificationIcon(): void {
    const img = new Image();
    img.onload = () => console.log('✅ Notification icon preloaded successfully');
    img.onerror = () => console.warn('⚠️ Failed to preload notification icon');
    img.src = this.STANDARD_ICON;
  }

  private loadPreferences(): void {
    const soundPref = localStorage.getItem('notification_sound_enabled');
    const browserPref = localStorage.getItem('browser_notifications_enabled');

    this.soundEnabled = soundPref !== null ? soundPref === 'true' : true;
    this.browserNotificationsEnabled = browserPref !== null ? browserPref === 'true' : true;
  }

  setSoundEnabled(enabled: boolean): void {
    this.soundEnabled = enabled;
    localStorage.setItem('notification_sound_enabled', enabled.toString());
  }

  setBrowserNotificationsEnabled(enabled: boolean): void {
    this.browserNotificationsEnabled = enabled;
    localStorage.setItem('browser_notifications_enabled', enabled.toString());
  }

  isSoundEnabled(): boolean {
    return this.soundEnabled;
  }

  isBrowserNotificationsEnabled(): boolean {
    return this.browserNotificationsEnabled;
  }

  async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.log('❌ Browser notifications not supported.');
      return false;
    }

    if (!this.browserNotificationsEnabled) {
      console.log('🔇 Browser notifications disabled by user preference.');
      return false;
    }

    try {
      // Check current permission status
      if (Notification.permission === 'granted') {
        console.log('✅ Browser notifications already granted.');
        return true;
      }

      if (Notification.permission === 'denied') {
        console.log('❌ Browser notifications permanently denied.');
        return false;
      }

      // Request permission with proper error handling
      const permission = await Notification.requestPermission();

      if (permission === 'granted') {
        console.log('✅ Browser notifications permission granted.');
        return true;
      } else {
        console.log('❌ Browser notifications permission denied.');
        return false;
      }
    } catch (error) {
      console.error('❌ Error requesting notification permission:', error);
      return false;
    }
  }

  /**
   * Standardized method to show message notifications
   * @param data - Message notification data
   */
  async showMessageNotification(data: MessageNotificationData): Promise<boolean> {
    const template = this.createMessageNotificationTemplate(data);
    return this.showStandardNotification(template, data.conversationId);
  }

  /**
   * Standardized method to show conversation notifications
   * @param data - Conversation notification data
   */
  async showConversationNotification(data: ConversationNotificationData): Promise<boolean> {
    const template = this.createConversationNotificationTemplate(data);
    return this.showStandardNotification(template, data.conversationId);
  }

  /**
   * Create standardized message notification template with state and type awareness
   * @param data - Message notification data with enhanced context
   */
  private createMessageNotificationTemplate(data: MessageNotificationData): NotificationTemplate {
    // Normalize text to remove special characters
    const normalizeText = (str: string): string =>
      str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    const title = normalizeText(`${data.campaignName} - ${data.senderName}`);

    // Enhanced body based on message type and status
    let body: string;
    let priority: 'low' | 'normal' | 'high' | 'urgent' = 'normal';
    let requireInteraction = false;

    // Determine message type display
    const getMessageTypePrefix = (messageType?: ContentMessageType): string => {
      if (messageType === undefined || messageType === null) return 'Nuevo mensaje';

      switch (messageType) {
        case ContentMessageType.Text:
          return 'Nuevo mensaje';
        case ContentMessageType.Image:
          return 'Nueva imagen';
        case ContentMessageType.Video:
          return 'Nuevo video';
        case ContentMessageType.Audio:
          return 'Nuevo audio';
        case ContentMessageType.Document:
          return 'Nuevo documento';
        case ContentMessageType.Unsupported:
          return 'Nuevo contenido';
        default:
          return 'Nuevo mensaje';
      }
    };

    const messagePrefix = getMessageTypePrefix(data.messageType);

    // Use utility method to determine priority and interaction requirements
    priority = this.getNotificationUrgency(data.conversationStatus, data.messageStatus, data.messageType) as 'low' | 'normal' | 'high' | 'urgent';
    requireInteraction = priority === 'urgent';

    // Enhanced body based on state
    if (data.conversationStatus === ConversationStatus.Pending) {
      body = `${messagePrefix} en conversación pendiente: ${data.messagePreview.slice(0, 80)}${data.messagePreview.length > 80 ? '...' : ''}`;
    } else if (data.messageStatus === 'Failed') {
      body = `⚠️ Error en mensaje: ${data.messagePreview.slice(0, 80)}${data.messagePreview.length > 80 ? '...' : ''}`;
    } else {
      body = `${messagePrefix}: ${data.messagePreview.slice(0, 100)}${data.messagePreview.length > 100 ? '...' : ''}`;
    }

    return {
      title,
      body,
      icon: this.STANDARD_ICON,
      tag: `message_${data.conversationId}_${data.messageType || 'text'}`,
      priority,
      requireInteraction
    };
  }

  /**
   * Create standardized conversation notification template with state awareness
   * @param data - Conversation notification data with status
   */
  private createConversationNotificationTemplate(data: ConversationNotificationData): NotificationTemplate {
    // Normalize text to remove special characters
    const normalizeText = (str: string): string =>
      str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    let title: string;
    let body: string;
    let priority: 'low' | 'normal' | 'high' | 'urgent' = 'normal';
    let requireInteraction = false;

    switch (data.status) {
      case ConversationStatus.Pending:
        title = normalizeText(`${data.campaignName} - Nueva Conversación`);
        body = `Conversación pendiente con ${data.clientName}`;
        priority = 'high';
        requireInteraction = true;
        break;

      case ConversationStatus.Active:
        if (data.previousStatus === ConversationStatus.Pending) {
          title = normalizeText(`${data.campaignName} - Conversación Asignada`);
          body = `Conversación asignada con ${data.clientName}`;
          priority = 'urgent';
          requireInteraction = true;
        } else {
          title = normalizeText(`${data.campaignName} - Conversación Activa`);
          body = `Conversación con ${data.clientName} actualizada`;
          priority = 'normal';
        }
        break;

      case ConversationStatus.Finish:
        title = normalizeText(`${data.campaignName} - Conversación Finalizada`);
        body = `Conversación con ${data.clientName} ha finalizado`;
        priority = 'low';
        break;

      default:
        title = normalizeText(`${data.campaignName} - ${data.clientName}`);
        body = 'Actualización de conversación';
        priority = 'normal';
    }

    return {
      title,
      body,
      icon: this.STANDARD_ICON,
      tag: `conversation_${data.conversationId}_${data.status}`,
      priority,
      requireInteraction
    };
  }

  /**
   * Core standardized notification method
   * @param template - Notification template
   * @param conversationId - Conversation ID for tracking
   */
  private async showStandardNotification(template: NotificationTemplate, conversationId?: string): Promise<boolean> {
    if (!this.browserNotificationsEnabled) {
      console.log('🔇 Notifications disabled by user preference');
      return false;
    }

    if (!('Notification' in window)) {
      console.log('❌ Browser notifications not supported');
      return false;
    }

    // Ensure we have permission
    if (Notification.permission !== 'granted') {
      const hasPermission = await this.requestPermission();
      if (!hasPermission) {
        this.showFallbackNotification(template.title, template.body);
        return true; // Consider fallback as success
      }
    }

    try {
      // Create unique notification key
      const notificationKey = template.tag || `${template.title}_${template.body.substring(0, 30)}`;

      // Apply debouncing
      const now = Date.now();
      if (now - this.lastNotificationTime < this.NOTIFICATION_DEBOUNCE_TIME) {
        console.log('🔇 Notification debounced - too soon since last notification');
        return false;
      }

      // Prevent duplicate notifications
      if (this.activeNotifications.has(notificationKey)) {
        console.log('🔇 Duplicate notification prevented:', template.title);
        return false;
      }

      this.lastNotificationTime = now;
      this.activeNotifications.add(notificationKey);

      // Create notification with standardized options and state awareness
      const notificationOptions: NotificationOptions = {
        body: template.body,
        icon: template.icon || this.STANDARD_ICON,
        badge: this.STANDARD_ICON,
        tag: `${notificationKey}_${now}`, // Always unique for Chrome compatibility
        requireInteraction: template.requireInteraction || template.priority === 'urgent',
        silent: false
      };

      console.log('� Creating standardized notification:', {
        title: template.title,
        body: template.body.substring(0, 50),
        priority: template.priority,
        conversationId,
        options: notificationOptions
      });

      const notification = new Notification(template.title, notificationOptions);

      // Set up standardized event handlers
      this.setupNotificationEventHandlers(notification, notificationKey, template);

      // Auto-cleanup after configured duration
      this.scheduleNotificationCleanup(notification, notificationKey);

      return true;

    } catch (error) {
      console.error('❌ Error creating standardized notification:', error);
      // Fallback to custom notification
      this.showFallbackNotification(template.title, template.body);
      return true;
    }
  }

  /**
   * Setup standardized event handlers for notifications
   */
  private setupNotificationEventHandlers(
    notification: Notification,
    notificationKey: string,
    template: NotificationTemplate
  ): void {
    notification.onclick = (event) => {
      console.log('📱 Notification clicked:', template.title);
      event.preventDefault();

      try {
        // Focus the window
        window.focus();
        if (window.parent && window.parent !== window) {
          window.parent.focus();
        }
      } catch (e) {
        console.warn('Could not focus window:', e);
      }

      notification.close();
    };

    notification.onshow = () => {
      console.log('📱 Notification shown successfully:', template.title);
    };

    notification.onerror = (error) => {
      console.error('❌ Notification error:', error);
      this.activeNotifications.delete(notificationKey);

      // Show fallback on error
      setTimeout(() => {
        this.showFallbackNotification(template.title, template.body);
      }, 100);
    };

    notification.onclose = () => {
      console.log('📱 Notification closed:', template.title);
      this.activeNotifications.delete(notificationKey);
    };
  }

  /**
   * Schedule automatic notification cleanup
   */
  private scheduleNotificationCleanup(notification: Notification, notificationKey: string): void {
    setTimeout(() => {
      if (this.activeNotifications.has(notificationKey)) {
        console.log('📱 Auto-closing notification after timeout');
        try {
          notification.close();
        } catch (e) {
          // Ignore close errors
        }
        this.activeNotifications.delete(notificationKey);
      }
    }, this.NOTIFICATION_DURATION);
  }

  /**
   * Legacy method maintained for backward compatibility
   * @deprecated Use showMessageNotification or showConversationNotification instead
   */
  async showNotification(title: string, body: string, iconPath?: string): Promise<boolean> {
    console.warn('⚠️ Using deprecated showNotification method. Please use showMessageNotification or showConversationNotification instead.');

    const template: NotificationTemplate = {
      title,
      body,
      icon: iconPath || this.STANDARD_ICON,
      tag: `legacy_${Date.now()}`,
      priority: 'normal'
    };

    return this.showStandardNotification(template);
  }

  async testNotification(): Promise<void> {
    console.log('🧪 Testing standardized notification system...');

    // Test message notification
    const messageTestData: MessageNotificationData = {
      senderName: 'Usuario de Prueba',
      campaignName: 'General',
      messagePreview: 'Este es un mensaje de prueba para verificar el funcionamiento del sistema de notificaciones.',
      conversationId: 'test-conversation-123'
    };

    const messageSuccess = await this.showMessageNotification(messageTestData);

    // Test conversation notification after a short delay
    setTimeout(async () => {
      const conversationTestData: ConversationNotificationData = {
        campaignName: 'General',
        clientName: 'Cliente de Prueba',
        conversationId: 'test-conversation-456',
        status: ConversationStatus.Active,
        previousStatus: ConversationStatus.Pending
      };

      const conversationSuccess = await this.showConversationNotification(conversationTestData);

      if (messageSuccess || conversationSuccess) {
        console.log('✅ Test notifications sent successfully');
      } else {
        console.log('❌ Test notifications failed');
      }
    }, 3000); // 3 second delay between test notifications
  }

  /**
   * Utility method to determine if a notification should be shown based on state
   * @param conversationStatus - Current conversation status
   * @param messageStatus - Current message status (optional)
   * @param messageType - Type of message (optional)
   */
  shouldShowNotificationForState(
    conversationStatus?: ConversationStatus,
    messageStatus?: MessageStatus,
    messageType?: ContentMessageType
  ): boolean {
    // Always show notifications for pending conversations (high priority)
    if (conversationStatus === ConversationStatus.Pending) {
      return true;
    }

    // Always show notifications for failed messages
    if (messageStatus === 'Failed') {
      return true;
    }

    // Show notifications for active conversations
    if (conversationStatus === ConversationStatus.Active) {
      // Skip notifications for read messages in active conversations to reduce noise
      if (messageStatus === 'Read') {
        return false;
      }
      return true;
    }

    // Don't show notifications for finished conversations unless it's a status change
    if (conversationStatus === ConversationStatus.Finish) {
      return false;
    }

    // Default to showing notification
    return true;
  }

  /**
   * Get notification urgency level based on state
   * @param conversationStatus - Current conversation status
   * @param messageStatus - Current message status (optional)
   * @param messageType - Type of message (optional)
   */
  getNotificationUrgency(
    conversationStatus?: ConversationStatus,
    messageStatus?: MessageStatus,
    messageType?: ContentMessageType
  ): 'low' | 'normal' | 'high' | 'urgent' {
    // Failed messages are urgent
    if (messageStatus === 'Failed') {
      return 'urgent';
    }

    // Pending conversations are urgent (need immediate attention)
    if (conversationStatus === ConversationStatus.Pending) {
      return 'urgent';
    }

    // Active conversations with multimedia content are high priority
    if (conversationStatus === ConversationStatus.Active) {
      if (messageType === ContentMessageType.Audio ||
          messageType === ContentMessageType.Video ||
          messageType === ContentMessageType.Document) {
        return 'high';
      }
      return 'normal';
    }

    // Finished conversations are low priority
    if (conversationStatus === ConversationStatus.Finish) {
      return 'low';
    }

    return 'normal';
  }
  getNotificationStatus(): any {
    const userAgent = navigator.userAgent.toLowerCase();
    return {
      supported: 'Notification' in window,
      permission: 'Notification' in window ? Notification.permission : 'not-supported',
      userPreferenceEnabled: this.browserNotificationsEnabled,
      soundEnabled: this.soundEnabled,
      browser: {
        isChrome: userAgent.includes('chrome') && !userAgent.includes('edge'),
        isEdge: userAgent.includes('edge'),
        isBrave: userAgent.includes('brave'),
        isSafari: userAgent.includes('safari') && !userAgent.includes('chrome'),
        isFirefox: userAgent.includes('firefox')
      },
      pageVisibility: {
        hidden: document.hidden,
        visibilityState: document.visibilityState,
        hasFocus: document.hasFocus()
      },
      activeNotifications: Array.from(this.activeNotifications),
      lastNotificationTime: this.lastNotificationTime,
      timeSinceLastNotification: Date.now() - this.lastNotificationTime
    };
  }
  private shouldShowNotification(): boolean {
    // Always show if page is not visible (user switched tabs/minimized)
    if (document.hidden || document.visibilityState === 'hidden') {
      return true;
    }

    // Check if window is focused
    if (!document.hasFocus()) {
      return true;
    }

    // For Chrome-based browsers, we may want to show notifications even when focused
    // to ensure they're not suppressed
    const userAgent = navigator.userAgent.toLowerCase();
    const isChromeBased = userAgent.includes('chrome') || userAgent.includes('edge') || userAgent.includes('brave');

    if (isChromeBased) {
      // Force show notifications in Chrome-based browsers
      return true;
    }

    // For other browsers (like Safari), follow standard behavior
    return !document.hasFocus();
  }

  /**
   * Alternative notification method for when browser notifications fail
   * Uses a custom in-page notification as fallback with standardized styling
   */
  private showFallbackNotification(title: string, body: string): void {
    // Create a temporary visual notification element
    const notification = document.createElement('div');
    notification.className = 'fallback-notification';
    notification.innerHTML = `
      <div class="fallback-notification-content">
        <div class="fallback-notification-header">
          <img src="${this.STANDARD_ICON}" alt="Ecopetrol Logo" class="fallback-notification-icon">
          <strong class="fallback-notification-title">${title}</strong>
        </div>
        <p class="fallback-notification-body">${body}</p>
      </div>
    `;

    // Add styles with enhanced design
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
      color: #2c3e50;
      padding: 16px;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.12);
      border: 1px solid rgba(255,255,255,0.18);
      z-index: 10000;
      max-width: 350px;
      min-width: 280px;
      animation: slideIn 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
      backdrop-filter: blur(10px);
      cursor: pointer;
    `;

    // Add enhanced animation styles if not already present
    if (!document.getElementById('fallback-notification-styles')) {
      const styles = document.createElement('style');
      styles.id = 'fallback-notification-styles';
      styles.textContent = `
        @keyframes slideIn {
          from {
            transform: translateX(100%) scale(0.95);
            opacity: 0;
          }
          to {
            transform: translateX(0) scale(1);
            opacity: 1;
          }
        }
        @keyframes slideOut {
          from {
            transform: translateX(0) scale(1);
            opacity: 1;
          }
          to {
            transform: translateX(100%) scale(0.95);
            opacity: 0;
          }
        }
        .fallback-notification-content {
          cursor: pointer;
        }
        .fallback-notification-header {
          display: flex;
          align-items: center;
          margin-bottom: 8px;
        }
        .fallback-notification-icon {
          width: 24px;
          height: 24px;
          margin-right: 10px;
          border-radius: 4px;
        }
        .fallback-notification-title {
          font-size: 16px;
          font-weight: 600;
          color: #2c3e50;
          margin: 0;
          flex: 1;
        }
        .fallback-notification-body {
          margin: 0;
          font-size: 14px;
          line-height: 1.4;
          color: #5a6c7d;
          margin-left: 34px;
        }
        .fallback-notification:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 40px rgba(0,0,0,0.15);
          transition: all 0.2s ease;
        }
      `;
      document.head.appendChild(styles);
    }

    // Add click handler to dismiss
    notification.addEventListener('click', () => {
      notification.style.animation = 'slideOut 0.3s ease-in';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    });

    document.body.appendChild(notification);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.style.animation = 'slideOut 0.3s ease-in';
        setTimeout(() => {
          if (notification.parentNode) {
            notification.parentNode.removeChild(notification);
          }
        }, 300);
      }
    }, 5000);

    console.log('📱 Fallback notification shown');
  }

  /**
   * Check if notifications are being blocked and provide helpful information
   */
  checkNotificationBlocking(): { blocked: boolean; reason: string; instructions?: string } {
    const userAgent = navigator.userAgent.toLowerCase();
    const isChromeBased = userAgent.includes('chrome') || userAgent.includes('edge') || userAgent.includes('brave');

    if (!('Notification' in window)) {
      return {
        blocked: true,
        reason: 'Browser does not support notifications'
      };
    }

    if (Notification.permission === 'denied') {
      const instructions = isChromeBased
        ? 'Click the bell icon in the address bar or go to Settings > Privacy and security > Site settings > Notifications'
        : 'Check your browser notification settings';

      return {
        blocked: true,
        reason: 'Notifications are blocked by user',
        instructions
      };
    }

    if (Notification.permission === 'default') {
      return {
        blocked: true,
        reason: 'Notification permission not yet granted',
        instructions: 'Please allow notifications when prompted'
      };
    }

    // Additional checks for Chrome-based browsers
    if (isChromeBased) {
      // Check if site is muted
      if (document.visibilityState === 'visible' && document.hasFocus()) {
        return {
          blocked: false,
          reason: 'Notifications should work, but Chrome may suppress them when tab is active'
        };
      }
    }

    return {
      blocked: false,
      reason: 'Notifications should work normally'
    };
  }
}
